import { AngleMeasurementsPlugin, DistanceMeasurementsPlugin } from "@xeokit/xeokit-sdk";
import { useContext, useEffect, useRef, useState } from "react";
import { ViewerContext } from "../../Context/ViewerContext/ViewerContextProvider";

import { AngleMeasurement } from "@xeokit/xeokit-sdk/types/plugins/AngleMeasurementsPlugin/AngleMeasurement";
import { DistanceMeasurement } from "@xeokit/xeokit-sdk/types/plugins/DistanceMeasurementsPlugin/DistanceMeasurement";
import { DraftingCompass, Magnet, PencilRuler, Ruler } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
    setInvisibleEntities,
    setOpenPlugin,
    setPickerIsEnabled,
    stopIsolatedMeasure,
} from "../../Context/ViewerContext/Reducer";
import { XktContext } from "../../Context/XktContext/XktContextProvider";
import { Separator } from "../ui/separator";
import { Toggle } from "../ui/toggle";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";

enum MeasurementType {
    DISTANCE = "distance",
    ANGLE = "ANGLE",
}

const Measurer = () => {
    const { state: viewerState, dispatch } = useContext(ViewerContext);
    const { state } = useContext(XktContext);
    const { t } = useTranslation();
    const [active, setActive] = useState<boolean>(false);
    const [snap, setSnap] = useState<boolean>(true);
    const [measurementType, setMeasurementType] = useState<MeasurementType>(MeasurementType.DISTANCE);
    const [distanceMeasurementsPlugin, setDistanceMeasurementsPlugin] = useState<DistanceMeasurementsPlugin | null>(
        null
    );
    const [angleMeasurementsPlugin, setAngleMeasurementsPlugin] = useState<AngleMeasurementsPlugin | null>(null);
    const [angleMeasurements, setAngleMeasurements] = useState<AngleMeasurement[]>([]);
    const [distanceMeasurements, setDistanceMeasurements] = useState<DistanceMeasurement[]>([]);
    const angleMeasurementsRef = useRef<AngleMeasurement[]>([]);
    const distanceMeasurementsRef = useRef<DistanceMeasurement[]>([]);
    angleMeasurementsRef.current = angleMeasurements;
    distanceMeasurementsRef.current = distanceMeasurements;
    useEffect(() => {
        if (viewerState.openPlugin !== "" && viewerState.openPlugin !== "Measurer" && active) {
            disableMeasure(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewerState.openPlugin]);

    const resolveMeasurer = () => {
        if (state.viewer) {
            const dmp = new DistanceMeasurementsPlugin(state.viewer, {});
            setDistanceMeasurementsPlugin(dmp);
            const amp = new AngleMeasurementsPlugin(state.viewer, {});
            setAngleMeasurementsPlugin(amp);
            amp.on("measurementCreated", (e: AngleMeasurement) => {
                setAngleMeasurements([...angleMeasurementsRef.current, e]);
            });
            dmp.on("measurementCreated", (e: DistanceMeasurement) => {
                setDistanceMeasurements([...distanceMeasurementsRef.current, e]);
            });
        } else {
            if (distanceMeasurementsPlugin) {
                distanceMeasurementsPlugin.destroy();
                setDistanceMeasurementsPlugin(null);
            }
        }
    };
    const enableMeasuring = () => {
        dispatch(setOpenPlugin("Measurer"));
        if (distanceMeasurementsPlugin && angleMeasurementsPlugin) {
            distanceMeasurementsPlugin.control.activate();
            setActive(true);
            setMeasurementType(MeasurementType.DISTANCE);
            distanceMeasurementsPlugin.control.snapping = true;
            angleMeasurementsPlugin.control.snapping = true;
            setSnap(true);
            dispatch(setPickerIsEnabled(false));
        }
    };
    const removeAllMarkersFromSight = () => {
        angleMeasurementsRef.current.forEach((m) => {
            angleMeasurementsPlugin?.destroyMeasurement(m.id);
        });
        distanceMeasurementsRef.current.forEach((m) => {
            distanceMeasurementsPlugin?.destroyMeasurement(m.id);
        });
        const markers = Array.from(document.querySelectorAll("#myMarkerDiv")) as HTMLElement[];
        for (const marker of markers) {
            marker.style.left = "-1000px";
            marker.style.top = "-1000px";
        }
    };
    const disableMeasure = (overrideOpenPlugin = true) => {
        if (overrideOpenPlugin) dispatch(setOpenPlugin(""));

        if (distanceMeasurementsPlugin && angleMeasurementsPlugin) {
            for (const m of angleMeasurements) {
                angleMeasurementsPlugin?.destroyMeasurement(m.id);
            }
            for (const m of distanceMeasurements) {
                distanceMeasurementsPlugin?.destroyMeasurement(m.id);
            }
            setAngleMeasurements([]);
            setDistanceMeasurements([]);

            dispatch(setPickerIsEnabled(true));
            distanceMeasurementsPlugin.control.deactivate();
            angleMeasurementsPlugin.control.deactivate();
            removeAllMarkersFromSight();
            setActive(false);
            if (viewerState.scheduleIsolatedMeasure) {
                dispatch(stopIsolatedMeasure());
                dispatch(setInvisibleEntities(viewerState.isolatedMeasureInvisibles));
            }
        }
    };
    const toggleType = (mst: MeasurementType) => {
        removeAllMarkersFromSight();
        if (distanceMeasurementsPlugin && angleMeasurementsPlugin) {
            if (mst === MeasurementType.ANGLE) {
                distanceMeasurementsPlugin.control.deactivate();
                angleMeasurementsPlugin.control.activate();
                setMeasurementType(MeasurementType.ANGLE);
            } else {
                angleMeasurementsPlugin.control.deactivate();
                distanceMeasurementsPlugin.control.activate();
                setMeasurementType(MeasurementType.DISTANCE);
            }
        }
    };
    const toggleSnap = () => {
        if (distanceMeasurementsPlugin && angleMeasurementsPlugin) {
            distanceMeasurementsPlugin.control.snapping = !snap;
            angleMeasurementsPlugin.control.snapping = !snap;

            setSnap(!snap);
        }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(resolveMeasurer, [state.viewer]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(resolveMeasurer, []);

    useEffect(() => {
        if (viewerState.scheduleIsolatedMeasure) {
            enableMeasuring();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewerState.scheduleIsolatedMeasure]);
    return (
        <>
            <Tooltip>
                <TooltipTrigger asChild>
                    <span className="inline-flex">
                        <Toggle
                            value="measure-tool"
                            aria-label={t("measurementsAriaLabel")}
                            className="mr-1"
                            pressed={active}
                            onPressedChange={() => (active ? disableMeasure() : enableMeasuring())}
                        >
                            <PencilRuler className="h-4 w-4" />
                        </Toggle>
                    </span>
                </TooltipTrigger>
                <TooltipContent>
                    <p>{t("measurements")}</p>
                </TooltipContent>
            </Tooltip>
            {active && (
                <>
                    <Separator orientation="vertical" className="py-4" />
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span className="inline-flex">
                                <Toggle
                                    value="angle-tool"
                                    aria-label={t("distanceAriaLabel")}
                                    className="ml-1 mr-1"
                                    pressed={measurementType == MeasurementType.DISTANCE}
                                    onPressedChange={() => toggleType(MeasurementType.DISTANCE)}
                                >
                                    <Ruler className="h-4 w-4" />
                                </Toggle>
                            </span>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p>{t("distance")}</p>
                        </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span className="inline-flex">
                                <Toggle
                                    value="angle-tool"
                                    aria-label={t("angleAriaLabel")}
                                    className="mr-1"
                                    pressed={measurementType == MeasurementType.ANGLE}
                                    onPressedChange={() => toggleType(MeasurementType.ANGLE)}
                                >
                                    <DraftingCompass className="h-4 w-4" />
                                </Toggle>
                            </span>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p>{t("angle")}</p>
                        </TooltipContent>
                    </Tooltip>

                    <Separator orientation="vertical" className="py-4" />
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span className="inline-flex">
                                <Toggle
                                    value="angle-tool"
                                    aria-label={t("snapToElementsAriaLabel")}
                                    className="mx-1"
                                    pressed={snap}
                                    onPressedChange={toggleSnap}
                                >
                                    <Magnet className="h-4 w-4" />
                                </Toggle>
                            </span>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p>{t("snapToElements")}</p>
                        </TooltipContent>
                    </Tooltip>
                </>
            )}
        </>
    );
};

export default Measurer;
